/**
 * @fileoverview Shared status table and rerun controls for per-department repair jobs.
 * @module system-web/components/dataRepair/DepartmentRepairStatusTable
 *
 * Responsibilities:
 * - Summarize repair states and render failed-first department rows.
 * - Provide selected and per-row rerun actions with running/submitting guards.
 * - Allow repair-specific columns without duplicating the shared table.
 *
 * Dependencies:
 * - React and lucide-react for presentation and action icons.
 * - useDepartmentRepairJobs row/status contracts.
 *
 * Architecture:
 * - Presentation-only; all data and actions are supplied by the caller.
 *
 * Notes:
 * - Shared Data Repair surface; repair-specific content belongs in extraColumns.
 */
import React, { useMemo, useState, type ReactNode } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import type { DepartmentRepairRow, DepartmentRepairStatus } from '../../hooks/useDepartmentRepairJobs';

interface DepartmentRepairStatusTableProps {
  rows: DepartmentRepairRow[];
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  notice: string | null;
  rerun: (tenantIds: string[]) => Promise<void>;
  extraColumns: Array<{ header: string; render: (row: DepartmentRepairRow) => ReactNode }>;
}

const STATUS_LABELS: Record<DepartmentRepairStatus, { label: string; color: string }> = {
  failed: { label: 'Failed', color: 'text-red-700 bg-red-50' },
  repaired: { label: 'Repaired', color: 'text-green-700 bg-green-50' },
  upToDate: { label: 'Up to date', color: 'text-blue-700 bg-blue-50' },
  skipped: { label: 'Skipped', color: 'text-amber-700 bg-amber-50' },
  running: { label: 'Running', color: 'text-orange-700 bg-orange-50' },
  notRun: { label: 'Not run', color: 'text-gray-600 bg-gray-100' },
};

function relativeTime(value?: string): string {
  if (!value) return '—';
  const date = Date.parse(value);
  if (!Number.isFinite(date)) return '—';
  const seconds = Math.max(0, Math.floor((Date.now() - date) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function lastRun(row: DepartmentRepairRow): string {
  if (!row.job) return '—';
  const trigger = row.job.payload?.trigger;
  const timestamp = row.job.completedAt ?? row.job.updatedAt ?? row.job.startedAt;
  return `${relativeTime(timestamp)}${trigger ? ` · ${trigger === 'startup' ? 'Startup' : 'Manual'}` : ''}`;
}

/**
 * @component DepartmentRepairStatusTable
 * Purpose: Render shared per-department repair status, summary counts, and rerun actions.
 * Props: rows, loading/submitting/error/notice state, rerun callback, and caller-provided extraColumns.
 * Behavior: Failed rows sort first; active rows and all rows during a submission cannot be rerun.
 * Data Source: Local (Dexie-derived rows supplied by a repair hook).
 * Constraints: No API or Dexie access; extra columns must remain presentation-only.
 * Performance: Counts and sorted rows are memoized from the supplied row set.
 *
 * @reusable
 * Usage: Shared Data Repair page for repairs whose per-department work fits this status model.
 * Rules: Presentation-only; callbacks and data are injected by the caller.
 */
export default function DepartmentRepairStatusTable({
  rows,
  isLoading,
  isSubmitting,
  error,
  notice,
  rerun,
  extraColumns,
}: DepartmentRepairStatusTableProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const sortedRows = useMemo(
    () => [...rows].sort((left, right) =>
      Number(right.status === 'failed') - Number(left.status === 'failed')
      || left.tenant.name.localeCompare(right.tenant.name)),
    [rows],
  );
  const counts = useMemo(() => ({
    failed: rows.filter((row) => row.status === 'failed').length,
    repaired: rows.filter((row) => row.status === 'repaired').length,
    upToDate: rows.filter((row) => row.status === 'upToDate').length,
    skipped: rows.filter((row) => row.status === 'skipped').length,
    running: rows.filter((row) => row.status === 'running').length,
    notRun: rows.filter((row) => row.status === 'notRun').length,
  }), [rows]);

  const eligibleSelectedIds = selectedIds.filter(
    (id) => rows.find((row) => row.tenant._id === id)?.status !== 'running',
  );
  const toggleSelected = (tenantId: string) => {
    setSelectedIds((current) => current.includes(tenantId)
      ? current.filter((id) => id !== tenantId)
      : [...current, tenantId]);
  };
  const rerunSelected = async () => {
    if (eligibleSelectedIds.length > 0) await rerun(eligibleSelectedIds);
  };

  return (
    <div>
      {error && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {notice && <div role="status" className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">{notice}</div>}
      <div className="grid grid-cols-2 gap-2 mb-5 sm:grid-cols-6">
        {([
          ['Failed', counts.failed],
          ['Repaired', counts.repaired],
          ['Up to date', counts.upToDate],
          ['Skipped', counts.skipped],
          ['Running', counts.running],
          ['Not run', counts.notRun],
        ] as const).map(([label, count]) => (
          <div key={label} className="rounded-lg border bg-white px-3 py-2">
            <div className="text-xs text-gray-500">{label}</div>
            <div className="text-lg font-semibold text-gray-900">{count}</div>
          </div>
        ))}
      </div>
      <div className="mb-3">
        <button
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50"
          disabled={eligibleSelectedIds.length === 0 || isSubmitting}
          onClick={() => void rerunSelected().catch(() => undefined)}
        >
          {isSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
          Re-run selected ({selectedIds.length})
        </button>
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading departments…</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="p-3"><span className="sr-only">Select</span></th>
                <th className="p-3">Department</th>
                <th className="p-3">Status</th>
                <th className="p-3">Last run</th>
                {extraColumns.map((column) => <th className="p-3" key={column.header}>{column.header}</th>)}
                <th className="p-3">Message</th>
                <th className="p-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedRows.map((row) => {
                const status = STATUS_LABELS[row.status];
                const running = row.status === 'running';
                return (
                  <tr key={row.tenant._id} className={row.status === 'failed' ? 'bg-red-50/30' : 'bg-white'}>
                    <td className="p-3">
                      <input
                        type="checkbox"
                        aria-label={`Select ${row.tenant.name}`}
                        checked={selectedIds.includes(row.tenant._id)}
                        onChange={() => toggleSelected(row.tenant._id)}
                      />
                    </td>
                    <td className="p-3 font-medium text-gray-900">{row.tenant.name}</td>
                    <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.color}`}>{status.label}</span></td>
                    <td className="p-3 whitespace-nowrap">{lastRun(row)}</td>
                    {extraColumns.map((column) => <td className="p-3" key={column.header}>{column.render(row)}</td>)}
                    <td className="p-3 max-w-xs">{row.job?.message ?? '—'}</td>
                    <td className="p-3">
                      <button
                        className="inline-flex items-center gap-1 whitespace-nowrap rounded border px-2 py-1 text-xs text-gray-700 disabled:opacity-40"
                        disabled={running || isSubmitting}
                        aria-label={`Re-run ${row.tenant.name}`}
                        onClick={() => void rerun([row.tenant._id]).catch(() => undefined)}
                      >
                        <RotateCcw className="h-3 w-3" /> Re-run
                      </button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={6 + extraColumns.length} className="p-6 text-center text-sm text-gray-500">No departments found in the local tenants table.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
